/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { gql } from '@apollo/client'

import { REVIEW_FEEDBACK_FRAGMENT } from '@dai/graphql'

const REVIEW_PAGER_FIELDS = gql`
  fragment ReviewPagerFields on ReviewPager {
    totalCount
    currentIndex
    hasPrevious
    hasNext
  }
`

const REVIEW_METADATA_FIELDS = gql`
  fragment ReviewMetadataFields on ReviewMetadata {
    workflow
    objectId
    verifiedAt
    verifiedById
    verifiedByEmail
    isVerified
    canEnterEditMode
    leasedByEmail
    statusMessage
  }
`

const REVIEW_DETECTION_SESSION_FIELDS = gql`
  fragment ReviewDetectionSessionFields on DetectionImageSession {
    canEdit
    statusMessage
    returnedFeedbackCount
    reviewFeedback {
      ...ReviewFeedbackFields
    }
    image {
      id
      imageUrl
      isFlagged
      flagged {
        level
        moreInfo
        reasons {
          type
          reason
          resolved
        }
      }
      leasedById
      leasedAt
      boundingBoxes {
        id
        sourceImageUrl
        minX
        maxX
        minY
        maxY
        metaClass
        tag
        tagId
        isHumanAdded
        isHumanDeleted
        leasedById
        leasedAt
      }
    }
  }
  ${REVIEW_FEEDBACK_FRAGMENT}
`

const REVIEW_CLASSIFICATION_BATCH_FIELDS = gql`
  fragment ReviewClassificationBatchFields on ClassificationBatch {
    upc
    activeUpc
    originalUpc
    anchorWasReplaced
    requiresAnchorReplacement
    rejectedBoxIds
    classificationReviewBatchId
    returnedFeedbackCount
    reviewFeedback {
      ...ReviewFeedbackFields
    }
    boundingBoxes {
      id
      sourceImageUrl
      minX
      maxX
      minY
      maxY
      metaClass
      isHumanAdded
      isHumanDeleted
      isFlagged
      flagged {
        level
        moreInfo
        reasons {
          type
          reason
          resolved
        }
      }
      leasedById
      leasedAt
    }
  }
  ${REVIEW_FEEDBACK_FRAGMENT}
`

const REVIEW_DETECTION_ITEM_FIELDS = gql`
  fragment ReviewDetectionItemFields on DetectionReviewItem {
    pager {
      ...ReviewPagerFields
    }
    reviewMetadata {
      ...ReviewMetadataFields
    }
    session {
      ...ReviewDetectionSessionFields
    }
  }
`

const REVIEW_CLASSIFICATION_ITEM_FIELDS = gql`
  fragment ReviewClassificationItemFields on ClassificationReviewItem {
    pager {
      ...ReviewPagerFields
    }
    reviewMetadata {
      ...ReviewMetadataFields
    }
    batch {
      ...ReviewClassificationBatchFields
    }
    classificationReviewBatchId
  }
`

const REVIEW_CORRECTION_ITEM_FIELDS = gql`
  fragment ReviewCorrectionItemFields on CorrectionReviewItem {
    pager {
      ...ReviewPagerFields
    }
    reviewMetadata {
      ...ReviewMetadataFields
    }
    correction {
      returnedFeedbackCount
      reviewFeedback {
        ...ReviewFeedbackFields
      }
      crop {
        id
        sourceImageUrl
        minX
        maxX
        minY
        maxY
        metaClass
        isHumanAdded
        isHumanDeleted
        isFlagged
        flagged {
          level
          moreInfo
          reasons {
            type
            reason
            resolved
          }
        }
        verifiedUpc
        currentUpc
        leasedById
        leasedAt
      }
      candidates {
        id
        upcA
        name
        brandName
        marketingImageUrl
        packaging
      }
      image {
        id
        imageUrl
      }
    }
  }
  ${REVIEW_FEEDBACK_FRAGMENT}
`

export const REVIEW_SEARCH_USER = gql`
  query ReviewSearchUser($email: String!) {
    reviewSearchUser(email: $email) {
      __typename
      ... on ReviewUserSearchResult {
        userId
        email
        firstName
        lastName
        workflowSummaries {
          workflow
          totalCount
        }
      }
      ... on NoItemsAvailableError {
        code
        message
      }
    }
  }
`

export const REVIEW_USER_WORKFLOW_ITEM = gql`
  query ReviewUserWorkflowItem(
    $email: String!
    $workflow: ReviewWorkflowEnum!
    $startDate: Date
    $endDate: Date
    $index: Int
  ) {
    reviewUserWorkflowItem(
      email: $email
      workflow: $workflow
      filters: { startDate: $startDate, endDate: $endDate }
      navigation: { index: $index }
    ) {
      __typename
      ... on DetectionReviewItem {
        ...ReviewDetectionItemFields
      }
      ... on ClassificationReviewItem {
        ...ReviewClassificationItemFields
      }
      ... on CorrectionReviewItem {
        ...ReviewCorrectionItemFields
      }
      ... on NoItemsAvailableError {
        code
        message
      }
    }
  }
  ${REVIEW_DETECTION_SESSION_FIELDS}
  ${REVIEW_CLASSIFICATION_BATCH_FIELDS}
  ${REVIEW_PAGER_FIELDS}
  ${REVIEW_METADATA_FIELDS}
  ${REVIEW_DETECTION_ITEM_FIELDS}
  ${REVIEW_CLASSIFICATION_ITEM_FIELDS}
  ${REVIEW_CORRECTION_ITEM_FIELDS}
`

export const REVIEW_OBJECT_ITEM = gql`
  query ReviewObjectItem($workflow: ReviewWorkflowEnum!, $objectId: ID!) {
    reviewObjectItem(workflow: $workflow, objectId: $objectId) {
      __typename
      ... on DetectionReviewItem {
        ...ReviewDetectionItemFields
      }
      ... on ClassificationReviewItem {
        ...ReviewClassificationItemFields
      }
      ... on CorrectionReviewItem {
        ...ReviewCorrectionItemFields
      }
      ... on NoItemsAvailableError {
        code
        message
      }
    }
  }
  ${REVIEW_DETECTION_SESSION_FIELDS}
  ${REVIEW_CLASSIFICATION_BATCH_FIELDS}
  ${REVIEW_PAGER_FIELDS}
  ${REVIEW_METADATA_FIELDS}
  ${REVIEW_DETECTION_ITEM_FIELDS}
  ${REVIEW_CLASSIFICATION_ITEM_FIELDS}
  ${REVIEW_CORRECTION_ITEM_FIELDS}
`

export const BEGIN_REVIEW_EDIT_DETECTION = gql`
  mutation BeginReviewEditDetection($imageId: ID!) {
    beginReviewEditDetection(imageId: $imageId) {
      __typename
      ... on DetectionImageSession {
        ...ReviewDetectionSessionFields
      }
      ... on LeaseLockError {
        code
        message
        retryAfterMs
      }
      ... on NoItemsAvailableError {
        code
        message
      }
    }
  }
  ${REVIEW_DETECTION_SESSION_FIELDS}
`

export const BEGIN_REVIEW_EDIT_CLASSIFICATION = gql`
  mutation BeginReviewEditClassification($batchId: ID!) {
    beginReviewEditClassification(batchId: $batchId) {
      __typename
      ... on ClassificationBatch {
        ...ReviewClassificationBatchFields
      }
      ... on LeaseLockError {
        code
        message
        retryAfterMs
      }
      ... on NoItemsAvailableError {
        code
        message
      }
    }
  }
  ${REVIEW_CLASSIFICATION_BATCH_FIELDS}
`

export const BEGIN_REVIEW_EDIT_CORRECTION = gql`
  mutation BeginReviewEditCorrection($boxId: ID!) {
    beginReviewEditCorrection(boxId: $boxId) {
      __typename
      ... on ClassificationCorrection {
        returnedFeedbackCount
        reviewFeedback {
          ...ReviewFeedbackFields
        }
        crop {
          id
          sourceImageUrl
          minX
          maxX
          minY
          maxY
          metaClass
          isHumanAdded
          isHumanDeleted
          isFlagged
          flagged {
            level
            moreInfo
            reasons {
              type
              reason
              resolved
            }
          }
          verifiedUpc
          currentUpc
          leasedById
          leasedAt
        }
        candidates {
          id
          upcA
          name
          brandName
          marketingImageUrl
          packaging
        }
        image {
          id
          imageUrl
        }
      }
      ... on LeaseLockError {
        code
        message
        retryAfterMs
      }
      ... on NoItemsAvailableError {
        code
        message
      }
    }
  }
  ${REVIEW_FEEDBACK_FRAGMENT}
`
